import React from 'react';
import Tooltip from '@atlaskit/tooltip';
import { css } from '@emotion/core';
import { token } from '@atlaskit/tokens';
import { IndicatorDTOHack } from '@atlassiansox/phobos-client-ts';

import { colors, flexStart, textTruncateAt } from '@/styles';

import { SearchableType } from '../composed/catalog-search-bar/types';
import { isIndicatorTypeMimir } from '../composed/slo-group/utils';

import LinkWithRef from './link';

const entityLinkInnerStyles = [
  css`
    color: ${token('color.text.subtle', colors.N500)};
  `,
];

type NavigableEntity = SearchableType | 'slo' | 'slo-group' | 'slo-mimir';

type EntityLinkProps = {
  publicId: string;
  name: string;
  scope: NavigableEntity;
  shouldTruncate?: boolean;
  truncateLength?: string;
  hideTooltip?: boolean;
  locationSearch?: string;
  indicator?: IndicatorDTOHack;
} & Pick<React.HTMLProps<HTMLDivElement>, 'className' | 'children'>;

export const entityLinkDefaultClassName = 'entity-link';

const EntityLink: React.FC<EntityLinkProps> = ({
  scope,
  publicId,
  name,
  shouldTruncate,
  truncateLength = '180px',
  children,
  className = entityLinkDefaultClassName,
  hideTooltip,
  locationSearch = '',
}) => (
  <LinkWithRef href={`/${scope}/${publicId}${locationSearch}`}>
    <Tooltip content={hideTooltip ? '' : `${scope} - ${name}`} position="right">
      <div css={flexStart}>
        <span
          css={[entityLinkInnerStyles, shouldTruncate ? textTruncateAt(truncateLength) : '']}
          className={className}
        >
          {name}
        </span>
        {children}
      </div>
    </Tooltip>
  </LinkWithRef>
);

export const TeamEntityLink: React.FC<Omit<EntityLinkProps, 'scope'>> = ({
  publicId,
  name,
  shouldTruncate,
  truncateLength,
  hideTooltip,
  locationSearch,
}) => (
  <EntityLink
    name={name}
    publicId={publicId}
    scope="team"
    shouldTruncate={shouldTruncate}
    truncateLength={truncateLength}
    hideTooltip={hideTooltip}
    locationSearch={locationSearch}
  />
);

export const ProductEntityLink: React.FC<Omit<EntityLinkProps, 'scope'>> = ({
  publicId,
  shouldTruncate,
  truncateLength,
  name,
  hideTooltip,
  locationSearch,
}) => (
  <EntityLink
    name={name}
    publicId={publicId}
    scope="product"
    shouldTruncate={shouldTruncate}
    truncateLength={truncateLength}
    hideTooltip={hideTooltip}
    locationSearch={locationSearch}
  />
);

export const SLOEntityLink: React.FC<Omit<EntityLinkProps, 'scope'>> = ({
  publicId,
  shouldTruncate,
  truncateLength,
  name,
  hideTooltip,
}) => (
  <EntityLink
    name={name}
    publicId={publicId}
    scope="slo"
    shouldTruncate={shouldTruncate}
    truncateLength={truncateLength}
    hideTooltip={hideTooltip}
  />
);

export const SLOGroupEntityLink: React.FC<Omit<EntityLinkProps, 'scope'>> = ({
  publicId,
  shouldTruncate,
  truncateLength,
  name,
  hideTooltip,
  indicator,
}) => (
  <EntityLink
    name={name}
    publicId={publicId}
    scope={`slo-${isIndicatorTypeMimir(indicator) ? 'mimir' : 'group'}`}
    shouldTruncate={shouldTruncate}
    truncateLength={truncateLength}
    hideTooltip={hideTooltip}
  />
);

export const CapabilityEntityLink: React.FC<Omit<EntityLinkProps, 'scope'>> = ({
  publicId,
  shouldTruncate,
  truncateLength = '330px',
  name,
  children,
  hideTooltip,
}) => (
  <EntityLink
    name={name}
    publicId={publicId}
    scope="capability"
    shouldTruncate={shouldTruncate}
    truncateLength={truncateLength}
    className={entityLinkDefaultClassName}
    hideTooltip={hideTooltip}
  >
    {children}
  </EntityLink>
);

export default EntityLink;
